import type { Theme } from '@material-ui/core'
import { makeStyles, Popover, useTheme } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye'
import type { LeaderboardRankResponse } from '@paintscout/api'
import type { DashboardEstimator } from '@paintscout/util/builder'
import React, { useState } from 'react'
import Skeleton from '@paintscout/ui/components/Skeleton'
import { useIsMobile } from '@paintscout/ui/hooks'
import type { StatCardProps } from '@paintscout/ui/components/StatCard'
import StatCard, { CardShim } from '@paintscout/ui/components/StatCard'
import Button from '@paintscout/ui/components/Button'
import Typography from '@paintscout/ui/components/Typography'
import { Grid } from '@paintscout/ui'
import GamifiedCallout from '../GamifiedCallout'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(),
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    minHeight: theme.spacing(23),
    justifyContent: 'space-between',
    position: 'relative'
  },
  primaryValue: {
    marginTop: theme.spacing(1.25),
    fontSize: theme.typography.pxToRem(48),
    fontWeight: 500
  },
  secondaryTitle: {
    opacity: 0.7
  },
  infoButton: {
    position: 'absolute',
    right: -theme.spacing(),
    top: theme.spacing(0.8),
    [theme.breakpoints.down('lg')]: {
      top: theme.spacing(1.4)
    },
    [theme.breakpoints.down('md')]: {
      top: theme.spacing(1.6)
    }
  },
  statExtrasValue: {
    fontSize: '24px',
    fontWeight: 500
  },
  gamifiedCalloutWrapper: {
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2)
  },
  popoverPaper: {},
  popoverHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    padding: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.common.white
  },
  closeIconDiv: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.common.white,
    transition: 'background-color 0.2s ease-in-out',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
      cursor: 'pointer'
    }
  }
}))

export interface StatCardGamifiedProps extends Omit<StatCardProps, 'infoIcon'> {
  statTitleLeft?: string
  statValueLeft?: string
  statTitleRight?: string
  statValueRight?: string
  popupContent?: React.ReactNode
  leaderboardRankData?: LeaderboardRankResponse
  selectedEstimators?: DashboardEstimator[]
  allEstimators?: DashboardEstimator[]
  handleJoinClick: () => void
}

function StatCardGamified(props: StatCardGamifiedProps) {
  const classes = useStyles(props)
  const theme = useTheme<Theme>()
  const {
    primaryTitle,
    secondaryTitle,
    loading,
    popupContent,
    primaryValue,
    secondaryValue,
    statTitleLeft,
    statValueLeft,
    statTitleRight,
    statValueRight,
    popoverAnchor,
    leaderboardRankData,
    showGamifiedCallouts,
    handleJoinClick,
    selectedEstimators,
    allEstimators
  } = props
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
  const notXl = useIsMobile({ custom: 1280 })
  const [openGamifiedCollapse, setOpenGamifiedCollapse] = useState(false)
  const open = Boolean(popoverAnchorEl)
  const cardContent = (
    <Grid container direction="column" justifyContent="flex-start" className={classes.root}>
      {popupContent && (
        <Popover
          classes={{ paper: classes.popoverPaper }}
          elevation={1}
          open={open}
          anchorEl={popoverAnchorEl}
          onClose={handleClose}
          anchorOrigin={
            popoverAnchor === 'right'
              ? {
                  vertical: 'center',
                  horizontal: 'right'
                }
              : { vertical: 'center', horizontal: 'left' }
          }
          transformOrigin={
            popoverAnchor === 'right'
              ? {
                  vertical: 'center',
                  horizontal: 'left'
                }
              : { vertical: 'center', horizontal: 'right' }
          }
        >
          <div className={classes.popoverHeader}>
            <div className={classes.closeIconDiv}>
              <CloseIcon onClick={() => handleClose()} />
            </div>
          </div>
          {popupContent}
        </Popover>
      )}
      <Button
        variant="text"
        className={classes.infoButton}
        icon={RemoveRedEyeIcon}
        onClick={handleClick}
        disabled={!popupContent}
      >
        <Typography variant="h6" weight={500} style={{ marginBottom: '-1px' }}>
          View
        </Typography>
      </Button>
      <Grid container spacing={0} justifyContent="space-between">
        <Grid item xs={12}>
          <Typography variant={notXl ? 'h2' : 'h3'}>{primaryTitle}</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={1} justifyContent="space-between">
        <Grid item xs={6} direction="column" justifyContent="flex-end">
          {loading ? (
            <Skeleton width={'145px'} height={'77.5px'} color={theme.paintscout.palette.brand.Neutral.P94} />
          ) : (
            <Typography showZeros={!primaryValue} variant="h1" className={classes.primaryValue}>
              {primaryValue}
            </Typography>
          )}
        </Grid>
        <Grid item xs={6}>
          {!loading && (
            <Grid item>
              {secondaryTitle && (
                <Typography variant="overline" gutterBottom={true}>
                  <span className={classes.secondaryTitle}>{secondaryTitle}</span>
                </Typography>
              )}
              {typeof secondaryValue !== 'undefined' &&
                (loading ? <CardShim /> : <Typography variant="h3">{secondaryValue}</Typography>)}
            </Grid>
          )}
        </Grid>
      </Grid>
      <br />
      {showGamifiedCallouts ? (
        <Grid item xs={12} style={{ marginBottom: '22px' }}>
          <GamifiedCallout
            topMargin={-1}
            open={openGamifiedCollapse}
            onToggleCollapse={(_ev, open) => setOpenGamifiedCollapse((prev) => (open ? open : !prev))}
            handleJoinClick={handleJoinClick}
            type="totalSold"
            data={leaderboardRankData}
            selectedEstimators={selectedEstimators}
            allEstimators={allEstimators}
          />
        </Grid>
      ) : (
        <Grid item></Grid>
      )}

      {statTitleLeft && statTitleRight && (
        <Grid item container spacing={2} style={{ marginTop: '0px', marginBottom: '-16px' }}>
          <Grid item container direction="column" xs={6} style={{ paddingBottom: '0px !important' }}>
            <Grid item>
              <Typography variant={'overline'} weight={500} noWrap>
                {statTitleLeft}
              </Typography>
            </Grid>
            <Grid item>
              {loading || !statValueLeft ? (
                <Skeleton width={'100%'} height={'33px'} color={theme.paintscout.palette.brand.Neutral.P94} />
              ) : (
                <span className={classes.statExtrasValue}>{statValueLeft}</span>
              )}
            </Grid>
          </Grid>
          <Grid item container direction="column" xs={6} justifyContent="flex-end" alignItems="flex-end">
            <Grid item>
              <Typography variant={'overline'} weight={500}>
                {statTitleRight}
              </Typography>
            </Grid>
            <Grid item>
              {loading || !statValueRight ? (
                <Skeleton width={'120px'} height={'33px'} color={theme.paintscout.palette.brand.Neutral.P94} />
              ) : (
                <span className={classes.statExtrasValue}>{statValueRight}</span>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  )

  function handleClick(e: React.MouseEvent) {
    setPopoverAnchorEl(e.currentTarget)
  }

  function handleClose() {
    setPopoverAnchorEl(null)
  }

  return (
    <StatCard
      style={{ minHeight: showGamifiedCallouts ? '285px' : notXl ? '219px' : '210px' }}
      {...props}
      classes={classes}
      cardContent={cardContent}
      variant="normal"
    />
  )
}

export default StatCardGamified
